import * as THREE from 'three';
import { ModelLoader } from '../human/ModelLoader.js';
import { ModelStorage } from '../utils/ModelStorage.js';

/**
 * 模型管理器
 * 负责模型导入、持久化存储、列表管理、切换、删除
 */
export class ModelManager {
  /** @type {Map<string, { scene: THREE.Group, animations: any[], name: string, thumbnail?: string, animationCount: number }>} */
  #models = new Map();
  /** @type {string | null} */
  #activeId = null;
  /** @type {ModelLoader} */
  #loader;
  /** @type {ModelStorage} */
  #storage;
  /** @type {HTMLElement} */
  #panel;
  /** @type {Record<string, Function>} */
  #callbacks;
  /** @type {boolean} 是否正在从存储恢复 */
  #restoring = false;

  constructor(callbacks = {}) {
    this.#loader = new ModelLoader();
    this.#storage = new ModelStorage();
    this.#callbacks = {
      onSelectModel: () => {},
      onRemoveModel: () => {},
      onModelLoaded: () => {},
      ...callbacks,
    };
    this.#createPanel();
    this.#bindEvents();
    this.#restoreSavedModels();
  }

  /** 创建模型管理面板 DOM */
  #createPanel() {
    const panel = document.createElement('div');
    panel.id = 'model-manager';
    panel.innerHTML = `
      <div class="mm-body" id="mm-body">
        <div class="mm-header">
          <span class="mm-title">&#128193; 模型管理</span>
        </div>
        <!-- 导入区域 -->
        <div class="mm-drop-zone" id="mm-drop-zone">
          <div class="mm-drop-icon">&#128228;</div>
          <div class="mm-drop-text">导入模型</div>
          <div class="mm-drop-sub">支持 .glb / .gltf 格式，数据持久保存</div>
          <button class="mm-import-btn" id="mm-import-btn">导入模型</button>
          <input type="file" id="mm-file-input" accept=".glb,.gltf" hidden>
        </div>
        <!-- 模型列表 -->
        <div class="mm-list" id="mm-list">
          <div class="mm-empty">尚未导入模型</div>
        </div>
      </div>
      <button class="mm-slide-toggle" id="mm-slide-toggle" title="展开">&#9654;</button>
    `;
    document.body.appendChild(panel);
    this.#panel = panel;
  }

  /** 绑定事件 */
  #bindEvents() {
    // 面板收起/展开（向左滑动隐藏）
    this.#panel.querySelector('#mm-slide-toggle')?.addEventListener('click', () => {
      const isCollapsed = this.#panel.classList.toggle('collapsed');
      const btn = this.#panel.querySelector('#mm-slide-toggle');
      if (btn) {
        btn.innerHTML = isCollapsed ? '&#9654;' : '&#9664;';
      }
    });

    // 文件选择按钮
    this.#panel.querySelector('#mm-import-btn')?.addEventListener('click', () => {
      this.#panel.querySelector('#mm-file-input')?.click();
    });

    // 文件选择
    this.#panel.querySelector('#mm-file-input')?.addEventListener('change', (e) => {
      const file = e.target.files?.[0];
      if (file) this.#handleFile(file);
      e.target.value = '';
    });

    // 拖拽上传
    const dropZone = this.#panel.querySelector('#mm-drop-zone');
    const body = document.body;

    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach((evt) => {
      body.addEventListener(evt, (e) => { e.preventDefault(); e.stopPropagation(); });
    });

    if (dropZone) {
      ['dragenter', 'dragover'].forEach((evt) => {
        dropZone.addEventListener(evt, () => dropZone.classList.add('dragging'));
      });
      ['dragleave', 'drop'].forEach((evt) => {
        dropZone.addEventListener(evt, () => dropZone.classList.remove('dragging'));
      });
      dropZone.addEventListener('drop', (e) => {
        const files = e.dataTransfer?.files;
        if (files?.length > 0) {
          const file = files[0];
          if (file.name.endsWith('.glb') || file.name.endsWith('.gltf')) {
            this.#handleFile(file);
          }
        }
      });
    }

    // 全局拖拽高亮
    body.addEventListener('dragenter', () => {
      this.#panel.classList.add('drag-global');
    });
    body.addEventListener('dragleave', (e) => {
      if (e.clientX <= 0 || e.clientY <= 0 ||
          e.clientX >= window.innerWidth || e.clientY >= window.innerHeight) {
        this.#panel.classList.remove('drag-global');
      }
    });
    body.addEventListener('drop', () => {
      this.#panel.classList.remove('drag-global');
    });

    // 模型列表操作（事件委托）
    this.#panel.querySelector('#mm-list')?.addEventListener('click', (e) => {
      const item = e.target.closest('.mm-item');
      if (!item) return;
      const id = item.dataset.id;

      if (e.target.closest('.mm-item-remove')) {
        e.stopPropagation();
        if (id) this.#removeModel(id);
        return;
      }

      if (id) this.#selectModel(id);
    });
  }

  /** 从 IndexedDB 恢复已保存的模型 */
  async #restoreSavedModels() {
    try {
      this.#restoring = true;
      const saved = await this.#storage.list();
      if (saved.length === 0) {
        this.#restoring = false;
        this.#renderList();
        return;
      }

      console.log(`[ModelManager] 正在恢复 ${saved.length} 个已保存的模型...`);
      for (const meta of saved) {
        const record = await this.#storage.get(meta.id);
        if (!record?.arrayBuffer) continue;

        const blob = new Blob([record.arrayBuffer], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const { scene, animations } = await this.#loader.load(url);
        URL.revokeObjectURL(url);

        this.#models.set(meta.id, {
          scene,
          animations,
          name: meta.name,
          fileName: meta.fileName,
          animationCount: meta.animationCount,
          thumbnail: meta.thumbnail || this.generateThumbnail(scene) || this.generateIcon(meta.name),
        });
      }

      this.#renderList();
      console.log(`[ModelManager] 已恢复 ${this.#models.size} 个模型`);
    } catch (err) {
      console.error('[ModelManager] 恢复模型失败:', err);
    } finally {
      this.#restoring = false;
    }
  }

  /** 生成缩略图（离屏渲染到小 Canvas） */
  generateThumbnail(scene) {
    try {
      const size = 128;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false, powerPreference: 'low-power' });
      renderer.setSize(size, size);
      renderer.setClearColor(0x0f2847, 1);
      renderer.setPixelRatio(1);

      const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 1000);
      const box = new THREE.Box3().setFromObject(scene);
      const center = box.getCenter(new THREE.Vector3());
      const bSize = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(bSize.x, bSize.y, bSize.z);
      if (maxDim <= 0) {
        renderer.dispose();
        return '';
      }
      const distance = maxDim / (2 * Math.tan((Math.PI / 4) * 0.45));

      camera.position.set(distance * 0.6, distance * 0.4, distance);
      camera.lookAt(center);
      camera.updateProjectionMatrix();

      // 临时添加灯光到场景
      const ambient = new THREE.AmbientLight(0xffffff, 1.5);
      const dir = new THREE.DirectionalLight(0xffffff, 2);
      dir.position.set(2, 3, 4);
      scene.add(ambient);
      scene.add(dir);

      // 确保材质更新
      scene.traverse((child) => {
        if (child.isMesh && child.material) {
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          materials.forEach((mat) => {
            mat.needsUpdate = true;
          });
        }
      });

      renderer.render(scene, camera);

      scene.remove(ambient);
      scene.remove(dir);

      renderer.dispose();
      const dataURL = canvas.toDataURL('image/png', 0.8);
      return dataURL.length > 100 ? dataURL : '';
    } catch (err) {
      console.warn('[ModelManager] 缩略图生成失败:', err);
      return '';
    }
  }

  /** 为模型生成彩色字母图标（兜底方案） */
  generateIcon(name) {
    try {
      const size = 128;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');

      // 渐变背景
      const colors = [
        ['#4fc3f7', '#0288d1'],
        ['#ab47bc', '#7b1fa2'],
        ['#66bb6a', '#388e3c'],
        ['#ffa726', '#f57c00'],
        ['#ef5350', '#d32f2f'],
        ['#7c5cff', '#5a3fd4'],
      ];
      const idx = (name || '').charCodeAt(0) % colors.length;
      const [c1, c2] = colors[idx];
      const grad = ctx.createLinearGradient(0, 0, size, size);
      grad.addColorStop(0, c1);
      grad.addColorStop(1, c2);
      ctx.fillStyle = grad;

      // 圆角矩形
      const r = 20;
      ctx.beginPath();
      ctx.moveTo(r, 0);
      ctx.lineTo(size - r, 0);
      ctx.quadraticCurveTo(size, 0, size, r);
      ctx.lineTo(size, size - r);
      ctx.quadraticCurveTo(size, size, size - r, size);
      ctx.lineTo(r, size);
      ctx.quadraticCurveTo(0, size, 0, size - r);
      ctx.lineTo(0, r);
      ctx.quadraticCurveTo(0, 0, r, 0);
      ctx.closePath();
      ctx.fill();

      // 首字母大写
      const letter = (name || '?').charAt(0).toUpperCase();
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 56px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(letter, size / 2, size / 2);

      return canvas.toDataURL('image/png', 0.9);
    } catch {
      return '';
    }
  }

  /** 处理导入的文件 */
  async #handleFile(file) {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['glb', 'gltf'].includes(ext)) {
      console.warn(`[ModelManager] 不支持的文件格式: ${ext}`);
      return;
    }

    const id = `${file.name.replace(/\.[^.]+$/, '')}-${Date.now()}`;

    try {
      const arrayBuffer = await file.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const { scene, animations } = await this.#loader.load(url);
      URL.revokeObjectURL(url);

      // 写入内存（同时生成缩略图）
      const thumbnail = this.generateThumbnail(scene) || this.generateIcon(file.name);
      this.#models.set(id, {
        scene,
        animations,
        name: file.name.replace(/\.[^.]+$/, ''),
        fileName: file.name,
        animationCount: animations.length,
        thumbnail,
      });

      // 持久化到 IndexedDB
      await this.#storage.save(id, {
        name: file.name.replace(/\.[^.]+$/, ''),
        fileName: file.name,
        animationCount: animations.length,
        arrayBuffer,
      });

      this.#renderList();
      this.#selectModel(id);
      this.#callbacks.onModelLoaded(id, this.#models.get(id));
    } catch (error) {
      console.error(`[ModelManager] 模型加载失败: ${file.name}`, error);
    }
  }

  /** 选中模型 */
  #selectModel(id) {
    const model = this.#models.get(id);
    if (!model) return;

    this.#activeId = id;
    this.#renderList();
    this.#callbacks.onSelectModel(id, model);
  }

  /** 删除模型 */
  async #removeModel(id) {
    const model = this.#models.get(id);
    if (!model) return;

    this.#models.delete(id);

    // 从 IndexedDB 删除
    await this.#storage.remove(id);

    if (this.#activeId === id) {
      this.#activeId = null;
      const nextId = this.#models.keys().next().value;
      if (nextId) this.#selectModel(nextId);
    }

    this.#callbacks.onRemoveModel(id, model);
    this.#renderList();
  }

  /** 渲染模型列表（缩略图+名称卡片） */
  #renderList() {
    const list = this.#panel.querySelector('#mm-list');
    if (!list) return;

    if (this.#models.size === 0) {
      list.innerHTML = this.#restoring
        ? '<div class="mm-empty">正在恢复已保存的模型...</div>'
        : '<div class="mm-empty">尚未导入模型</div>';
      return;
    }

    list.innerHTML = '';
    for (const [id, model] of this.#models) {
      const isActive = id === this.#activeId;
      const item = document.createElement('div');
      item.className = `mm-item${isActive ? ' active' : ''}`;
      item.dataset.id = id;

      const thumbHTML = model.thumbnail
        ? `<img class="mm-thumb-img" src="${model.thumbnail}" alt="${model.name}" loading="lazy">`
        : `<div class="mm-thumb-placeholder"><span class="mm-thumb-icon">&#128283;</span></div>`;

      item.innerHTML = `
        <div class="mm-thumb">${thumbHTML}</div>
        <div class="mm-item-info">
          <div class="mm-item-name" title="${model.name}">${model.name}</div>
          <div class="mm-item-meta">${model.animationCount} 个动画</div>
        </div>
        <button class="mm-item-remove" title="移除">&#10005;</button>
      `;
      list.appendChild(item);
    }
  }

  // ============ 公开 API ============

  get loader() {
    return this.#loader;
  }

  /**
   * 注册模型到管理器（供外部预加载使用，不持久化）
   * @param {string} id
   * @param {object} modelData
   * @param {THREE.Group} modelData.scene
   * @param {THREE.AnimationClip[]} modelData.animations
   * @param {string} modelData.name
   * @param {string} modelData.fileName
   * @param {number} modelData.animationCount
   * @param {string} [modelData.thumbnail] - base64 缩略图
   */
  registerModel(id, modelData) {
    this.#models.set(id, { ...modelData, thumbnail: modelData.thumbnail || '' });
    this.#renderList();
  }

  /** 选中指定模型 */
  selectModel(id) {
    this.#selectModel(id);
  }

  /** 获取当前选中模型 ID */
  getActiveId() {
    return this.#activeId;
  }

  /** 根据 ID 获取模型数据 */
  getModel(id) {
    return this.#models.get(id) ?? null;
  }

  /** 获取所有已加载的模型 */
  getAllModels() {
    return new Map(this.#models);
  }

  /** 获取当前激活的模型 */
  getActiveModel() {
    return this.#activeId ? this.#models.get(this.#activeId) ?? null : null;
  }

  /** 销毁面板 */
  dispose() {
    this.#panel?.remove();
    this.#panel = null;
  }
}
